// CAPA Tracker — read-only view of the RCA investigations raised for this
// centre, with their root cause, contributing factors and evidence.
//
// WHAT IT SHOWS, and why it is RCA data under a CAPA heading:
// QTQMS has two APIs. /api/capa/records holds the corrective and preventive
// actions, but every one of its records has `client_code: null`, so filtering
// it per centre returns nothing. /api/rca/records does carry the client, so
// that is the one the backend calls. These records have no action lists — the
// fields are the investigation itself — so the card is built around those
// instead of the two action columns the CAPA shape would have had.
//
// READ-ONLY. QTQMS's own screen has Reopen and delete; our API is GET-only.

import { useState } from 'react';
import {
  Box, Stack, Typography, TextField, InputAdornment, LinearProgress,
  Skeleton, Grid, Link, Collapse, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useListCapaQuery } from '../../../store/capaApi';
import {
  MP, MONO, SectionCard, CardHeader, StatCard, Pill, TabButton, Alert, prettyDate,
} from './shared';

// The lifecycle states on view_status_label — the same set Report Queries
// uses, so the two screens agree.
const TABS = [
  { key: '',              label: 'All' },
  { key: 'received',      label: 'Received' },
  { key: 'investigating', label: 'Investigating' },
  { key: 'resolved',      label: 'Resolved' },
  { key: 'closed',        label: 'Closed' },
];

const STATUS_TONE = {
  received:      'orange',
  investigating: 'yellow',
  resolved:      'green',
  closed:        'teal',
};

// L1 is the mildest, L4/L5 the most serious. Colour only above L2 so a
// routine investigation does not shout.
const SEVERITY_TONE = (sev) => {
  const n = Number(String(sev || '').replace(/\D/g, ''));
  if (!n) return 'teal';
  if (n >= 4) return 'red';
  if (n === 3) return 'orange';
  return 'teal';
};

const titleCase = (s) =>
  String(s || '').replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ISO timestamp → "21 Oct 2026, 16:57". Falls back to the raw value so a
// format we have not seen still shows something.
const prettyWhen = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return `${prettyDate(d.toISOString().slice(0, 10))}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// A labelled block of prose. Rendered only when there is something to show —
// RCA records leave most of these null until the investigation progresses,
// and empty headings make the card look broken.
const Field = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography
        sx={{
          fontFamily: MONO, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
          color: MP.textMuted, textTransform: 'uppercase', mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO, fontSize: 13, lineHeight: 1.6, color: MP.text,
          whiteSpace: 'pre-line',   // QTQMS stores real line breaks in these
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

// Small key/value used in the meta strip.
const Meta = ({ label, value }) => {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: MP.text }}>
        {value}
      </Typography>
    </Stack>
  );
};

const RcaCard = ({ record }) => {
  const [open, setOpen] = useState(false);

  const view = String(record?.view_status_label || record?.status || '').toLowerCase();
  const hasDetail = Boolean(
    record?.root_cause || record?.external_root_cause
    || record?.contributing_factors || record?.apparent_cause
    || record?.attachments_count,
  );

  return (
    <SectionCard sx={{ mb: 2, borderLeft: `4px solid ${MP.teal}`, p: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        {/* ── Identity row ── */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 800, color: MP.teal }}>
              {record?.code}
            </Typography>
            {record?.nc_id && (
              <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MP.textMuted }}>
                {record.nc_id}
              </Typography>
            )}
            <Pill tone={STATUS_TONE[view] || 'teal'}>{titleCase(view)}</Pill>
            {record?.severity && (
              <Pill tone={SEVERITY_TONE(record.severity)}>{record.severity}</Pill>
            )}
            {record?.phase && <Pill tone="purple">{titleCase(record.phase)}</Pill>}
            {record?.source && <Pill tone="teal">{record.source}</Pill>}
          </Stack>

          <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted, whiteSpace: 'nowrap' }}>
            {record?.submitted_at
              ? `Submitted ${prettyWhen(record.submitted_at)}`
              : record?.created_at ? `Raised ${prettyWhen(record.created_at)}` : ''}
          </Typography>
        </Stack>

        {/* ── What happened ── */}
        <Field label="Description" value={record?.description} />

        {/* ── Meta strip ── */}
        <Stack
          direction="row"
          spacing={2.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1, pt: 1, borderTop: `1px solid ${MP.border}` }}
        >
          <Meta label="Branch" value={record?.branch} />
          <Meta label="Detected by" value={record?.detector} />
          <Meta label="Detected" value={prettyWhen(record?.detected_at)} />
          <Meta label="Occurred" value={prettyWhen(record?.occurred_at)} />
          {record?.impact && <Meta label="Impact" value={titleCase(record.impact)} />}
          {record?.attachments_count > 0 && (
            <Meta label="Evidence" value={`${record.attachments_count} file${record.attachments_count === 1 ? '' : 's'}`} />
          )}
        </Stack>
      </Box>

      {/* ── Investigation detail, collapsed by default ── */}
      {hasDetail && (
        <>
          <Divider />
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((o) => !o); }}
            sx={{
              px: 2, py: 1, cursor: 'pointer', bgcolor: MP.tealLight,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              '&:hover': { bgcolor: MP.tealMid },
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO, fontSize: 11.5, fontWeight: 800,
                letterSpacing: 0.6, color: MP.darkMid, textTransform: 'uppercase',
              }}
            >
              {open ? 'Hide investigation' : 'View investigation'}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 20, color: MP.darkMid,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}
            />
          </Box>

          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: MP.white }}>
              <Field label="Root cause"           value={record?.root_cause} />
              <Field label="External root cause"  value={record?.external_root_cause} />
              <Field label="Apparent cause"       value={record?.apparent_cause} />
              <Field label="Contributing factors" value={record?.contributing_factors} />

              {Array.isArray(record?.attachments) && record.attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: MONO, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
                      color: MP.textMuted, textTransform: 'uppercase', mb: 0.5,
                    }}
                  >
                    Evidence
                  </Typography>
                  <Stack spacing={0.5}>
                    {record.attachments.map((a, i) => (
                      <Stack key={a.url || i} direction="row" spacing={0.75} alignItems="center">
                        <AttachFileIcon sx={{ fontSize: 14, color: MP.textMuted }} />
                        <Link
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: MONO, fontSize: 12.5, color: MP.teal,
                            wordBreak: 'break-all',
                          }}
                        >
                          {a.name || `Attachment ${i + 1}`}
                        </Link>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Meta label="Method" value={titleCase(record?.method)} />
                <Meta label="Root cause declared" value={prettyWhen(record?.root_cause_declared_at)} />
                <Meta label="Completed" value={record?.date_completed ? prettyDate(record.date_completed) : ''} />
                <Meta label="Submitted by" value={record?.submitted_by} />
                <Meta label="Verified by" value={record?.verified_by} />
              </Stack>
            </Box>
          </Collapse>
        </>
      )}
    </SectionCard>
  );
};

// Loading skeleton — mirrors the real layout so nothing shifts on load.
const CapaSkeleton = () => (
  <>
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Grid item xs={6} sm={4} md={2.4} key={`t-${i}`}>
          <Box sx={{ p: 1.5, bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: 2 }}>
            <Skeleton variant="text" width="60%" height={12} />
            <Skeleton variant="text" width={40} height={30} />
          </Box>
        </Grid>
      ))}
    </Grid>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
      <Skeleton variant="rounded" height={38} sx={{ flex: 1 }} />
      <Stack direction="row" spacing={1}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={`tab-${i}`} variant="rounded" width={78} height={32} />
        ))}
      </Stack>
    </Stack>
    {Array.from({ length: 2 }).map((_, i) => (
      <SectionCard key={`c-${i}`} sx={{ mb: 2, borderLeft: `4px solid ${MP.tealMid}` }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Skeleton variant="text" width={72} height={20} />
          <Skeleton variant="rounded" width={78} height={20} />
          <Skeleton variant="rounded" width={40} height={20} />
        </Stack>
        <Skeleton variant="text" width="90%" height={14} />
        <Skeleton variant="text" width="75%" height={14} />
        <Stack direction="row" spacing={2} sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${MP.border}` }}>
          {[0, 1, 2, 3].map((k) => (
            <Skeleton key={k} variant="text" width={110} height={12} />
          ))}
        </Stack>
      </SectionCard>
    ))}
  </>
);

const CapaTracker = () => {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching, error } = useListCapaQuery(
    { status, search: search.trim(), perPage: 100 },
    { refetchOnMountOrArgChange: true },
  );

  const counts = data?.counts || {};
  const records = data?.records || [];

  // Tiles follow the lifecycle, not draft/submitted — the same states as the
  // tabs beneath them, so a number and its tab always agree.
  const tiles = [
    { label: 'Total',         value: counts.total ?? 0,         tone: 'teal' },
    { label: 'Received',      value: counts.received ?? 0,      tone: 'orange' },
    { label: 'Investigating', value: counts.investigating ?? 0, tone: 'yellow' },
    { label: 'Resolved',      value: counts.resolved ?? 0,      tone: 'green' },
    { label: 'Closed',        value: counts.closed ?? 0,        tone: 'teal' },
  ];

  if (isLoading) return <CapaSkeleton />;

  return (
    <>
      <Alert tone="info" sx={{ mb: 2 }}>
        Root-cause investigations raised for your centre. Managed in TrustLab
        QMS — this view is read-only.
      </Alert>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <Grid item xs={6} sm={4} md={2.4} key={t.label}>
            <StatCard label={t.label} value={t.value} tone={t.tone} />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: MP.radius,
          boxShadow: MP.shadow,
          p: '13px',
          mb: 1.75,
        }}
      >
        <TextField
          size="small"
          placeholder="Search code, NC ID or root cause…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontFamily: MONO, fontSize: 13.5 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MP.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ borderBottom: `1px solid ${MP.border}`, mb: 2 }}>
        <Stack direction="row" spacing={0} flexWrap="wrap" useFlexGap>
          {TABS.map((t) => (
            <TabButton
              key={t.key || 'all'}
              active={status === t.key}
              onClick={() => setStatus(t.key)}
              badge={t.key ? counts[t.key] : counts.total}
            >
              {t.label}
            </TabButton>
          ))}
        </Stack>
      </Box>

      {error && (
        <Alert tone="error" sx={{ mb: 2 }}>
          {error?.data?.error || 'Could not load investigations. Please try again.'}
        </Alert>
      )}

      {isFetching && (
        <LinearProgress sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: MP.teal } }} />
      )}

      {!error && records.length === 0 && (
        <SectionCard>
          <Typography
            sx={{ fontFamily: MONO, fontSize: 13.5, color: MP.textMuted, py: 2, textAlign: 'center' }}
          >
            {search || status
              ? 'No investigations match this filter.'
              : 'No investigations have been raised for your centre yet.'}
          </Typography>
        </SectionCard>
      )}

      {records.length > 0 && (
        <SectionCard>
          <CardHeader
            title="Root-cause Investigations"
            subtitle={`${records.length} ${records.length === 1 ? 'record' : 'records'} shown`}
          />
          <Box sx={{ p: '15px' }}>
            {records.map((r) => <RcaCard key={r.id || r.code || r.nc_id} record={r} />)}
          </Box>
        </SectionCard>
      )}
    </>
  );
};

export default CapaTracker;
